import { SelectedOptions } from '@shopify/hydrogen-react/ProductProvider';
import {
  Product,
  ProductOption,
  ProductVariant,
  SelectedOption,
} from '@shopify/hydrogen-react/storefront-api-types';
import { flatResponseEdges } from '../../utils/storefront/flatResponseEdges';

/**
 * The Storefront API returns `optionValues` in an order that doesn't always match
 * the merchant-intended order (e.g. sizes come back as L, S, XL, M). The variants
 * list, however, is ordered. This reorders each option's values by the sequence in
 * which they first appear across the ordered variants. Values that never appear in
 * a variant keep their original relative position at the end.
 */
export const orderOptionValuesByVariants = (product: Product): ProductOption[] => {
  const variants = flatResponseEdges(product.variants.edges) as ProductVariant[];

  return product.options.map(option => {
    const variantOrder: string[] = [];

    variants.forEach(variant => {
      const value = getVariantOption(variant, option.name)?.value;
      if (value && !variantOrder.includes(value)) {
        variantOrder.push(value);
      }
    });

    if (!variantOrder.length) {
      return option;
    }

    const rank = (name: string) => {
      const index = variantOrder.indexOf(name);
      return index === -1 ? variantOrder.length : index;
    };

    return {
      ...option,
      optionValues: [...option.optionValues].sort((a, b) => rank(a.name) - rank(b.name)),
    };
  });
};

export const getFlattenVariants = (variants: ProductVariant[], variantName) => {
  const existingVariants: string[] = [];

  return variants?.reduce((a, i) => {
    const current: string | undefined = i?.selectedOptions?.find(
      item => item?.name === variantName
    )?.value;

    if (current && !existingVariants.includes(current)) {
      existingVariants.push(current);
      a.push(i as ProductVariant);
      return a;
    }
    return a;
  }, [] as ProductVariant[]);
};

export const findVariantByOptions = (
  product: Product,
  options: SelectedOptions
): ProductVariant | null => {
  if (!options) {
    return null;
  }

  let variants = flatResponseEdges(product.variants.edges);

  return (
    variants.find((variant: ProductVariant) => {
      return Object.entries(options).every(([key, value]) =>
        variant.selectedOptions.some(obj => obj.name === key && obj.value === value)
      );
    }) || null
  );
};

export const optionCombinationExists = (
  availableOptions: SelectedOption[][],
  selectedOptions: SelectedOptions,
  name: string,
  value: string
): boolean => {
  // Create a new combination of options as an object
  const newCombination: SelectedOptions = { ...selectedOptions, [name]: value };

  // Convert each available option set to an object for easier comparison
  const availableOptionsAsObjects: SelectedOptions[] = availableOptions.map(optionSet =>
    optionSet.reduce((acc: SelectedOptions, option) => {
      acc[option.name] = option.value;
      return acc;
    }, {})
  );

  // Check if this new combination of options exists in the available options
  return availableOptionsAsObjects.some(optionSet =>
    Object.keys(newCombination).every(key => optionSet[key] === newCombination[key])
  );
};

export const handleOptionSelect = (
  selectedProductOptions?: SelectedOptions,
  key?: string,
  value?: string
) => {
  const updatedOptions = { ...selectedProductOptions };

  if (key && value) {
    if (updatedOptions[key] === value) {
      delete updatedOptions[key];
    } else {
      updatedOptions[key] = value;
    }
  }

  return updatedOptions;
};

export const getOptionsFromVariant = (variant: ProductVariant): SelectedOptions => {
  return variant.selectedOptions.reduce(
    (a, i) => {
      a[i.name] = i.value;
      return a;
    },
    {} as { [key: string]: string }
  );
};

export const getVariantOption = (
  productVariant: ProductVariant,
  option: string
): SelectedOption | undefined => {
  return productVariant.selectedOptions.find(selectedOption => {
    return selectedOption.name === option;
  });
};

export const createCanonicalUrl = (product: Product, productVariant?: ProductVariant): string => {
  const options = productVariant ? getOptionsFromVariant(productVariant) : '';
  const searchParams = new URLSearchParams(options).toString();
  let base = process.env.REACT_APP_API_URL + '/product/' + product.handle;

  return searchParams.length ? base + '?' + searchParams : base;
};

export const optionsToQueryString = (options: SelectedOptions): string => {
  const keys = Object.keys(options);
  const keyValuePairs = keys.map(key => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(options[key]);
  });
  return keyValuePairs.join('&');
};
